/**
 * Carousel controls bound to the nearest slideshow-list
 * inside [data-carousel-root] (or a parent slideshow-list).
 *
 *   <carousel-navigation>  — prev/next
 *   <carousel-scrollbar>   — progress / scrollbar
 *   <carousel-pagination>  — bullets
 *
 * slideshow-list auto-wires these at init. These elements re-attach
 * when they connect after ready, or after destroy/reinit (stack mobile).
 */

function getSlideshow(el) {
  const root = el.closest('[data-carousel-root]');
  return root ? root.querySelector('slideshow-list') : el.closest('slideshow-list');
}

function whenSlideshowReady(el, callback) {
  const slideshow = getSlideshow(el);
  if (!slideshow) return null;

  const onReady = (event) => callback(event.detail.swiper, event.detail.slideshow);

  if (slideshow.swiper) {
    callback(slideshow.swiper, slideshow);
  } else if (slideshow.classList.contains('is-ready')) {
    callback(null, slideshow);
  }

  slideshow.addEventListener('slideshow-list:ready', onReady);
  el._slideshowReadyHandler = { slideshow, onReady };

  return slideshow;
}

function stopSlideshowReady(el) {
  const binding = el._slideshowReadyHandler;
  if (!binding) return;

  binding.slideshow.removeEventListener('slideshow-list:ready', binding.onReady);
  el._slideshowReadyHandler = null;
}

function reinitModule(module, initFn) {
  if (!module) return;
  module.destroy();
  initFn();
}

if (!customElements.get('carousel-navigation')) {
  class CarouselNavigation extends HTMLElement {
    connectedCallback() {
      this.prevBtn = this.querySelector('[data-swiper-prev]');
      this.nextBtn = this.querySelector('[data-swiper-next]');
      if (!this.prevBtn || !this.nextBtn) return;

      if (getSlideshow(this)) {
        whenSlideshowReady(this, (swiper) => {
          if (swiper) this.attach(swiper);
        });
        return;
      }

      // Delegated mode: not inside a carousel root (e.g. section header).
      // Follow the currently visible slideshow-list in the section,
      // re-attaching whenever tabs change or a carousel becomes ready.
      this.scope = this.closest('[data-section]') || this.closest('.shopify-section');
      if (!this.scope) return;

      this.onScopeUpdate = () => this.syncToVisible();
      this.scope.addEventListener('slideshow-list:ready', this.onScopeUpdate);
      this.scope.addEventListener('tabbed-content:change', this.onScopeUpdate);
      this.syncToVisible();
    }

    disconnectedCallback() {
      stopSlideshowReady(this);

      if (this.scope) {
        this.scope.removeEventListener('slideshow-list:ready', this.onScopeUpdate);
        this.scope.removeEventListener('tabbed-content:change', this.onScopeUpdate);
        this.scope = null;
      }
    }

    /** Delegated mode: bind to the first visible slideshow-list's swiper. */
    syncToVisible() {
      for (const list of this.scope.querySelectorAll('slideshow-list')) {
        if (list.offsetWidth === 0 && list.offsetHeight === 0) continue;
        if (list.swiper && !list.swiper.destroyed) this.attach(list.swiper);
        // If the swiper isn't ready yet, the ready event re-triggers this.
        return;
      }
    }

    attach(swiper) {
      const params = swiper.params.navigation;
      const alreadyBound = params?.prevEl === this.prevBtn && params?.nextEl === this.nextBtn;

      if (alreadyBound) {
        swiper.navigation?.update();
        return;
      }

      this.releasePrevious(swiper);
      this.boundSwiper = swiper;

      swiper.params.navigation = {
        ...(params || {}),
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
      };

      reinitModule(swiper.navigation, () => {
        swiper.navigation.init();
        swiper.navigation.update();
      });
    }

    /** Unbind these buttons from a previously controlled swiper (tab switch). */
    releasePrevious(nextSwiper) {
      const prev = this.boundSwiper;
      if (!prev || prev === nextSwiper || prev.destroyed) return;
      if (prev.params.navigation?.prevEl !== this.prevBtn) return;

      prev.navigation?.destroy();
      prev.params.navigation = {
        ...prev.params.navigation,
        prevEl: null,
        nextEl: null,
      };
    }
  }

  customElements.define('carousel-navigation', CarouselNavigation);
}

if (!customElements.get('carousel-scrollbar')) {
  class CarouselScrollbar extends HTMLElement {
    connectedCallback() {
      this.toggleAttribute('data-swiper-scrollbar', true);

      whenSlideshowReady(this, (swiper) => {
        if (swiper) this.attach(swiper);
      });
    }

    disconnectedCallback() {
      stopSlideshowReady(this);
    }

    attach(swiper) {
      const params = swiper.params.scrollbar;
      if (params?.el === this) {
        swiper.scrollbar?.updateSize();
        return;
      }

      swiper.params.scrollbar = {
        ...(params || {}),
        el: this,
        draggable: true,
        dragClass: 'slideshow-list-swiper__scrollbar-drag',
      };

      reinitModule(swiper.scrollbar, () => {
        swiper.scrollbar.init();
        swiper.scrollbar.updateSize();
      });
    }
  }

  customElements.define('carousel-scrollbar', CarouselScrollbar);
}

if (!customElements.get('carousel-pagination')) {
  class CarouselPagination extends HTMLElement {
    connectedCallback() {
      this.toggleAttribute('data-swiper-pagination', true);

      whenSlideshowReady(this, (swiper) => {
        if (swiper) this.attach(swiper);
      });
    }

    disconnectedCallback() {
      stopSlideshowReady(this);
    }

    attach(swiper) {
      const params = swiper.params.pagination;
      if (params?.el === this) {
        swiper.pagination?.render();
        swiper.pagination?.update();
        return;
      }

      swiper.params.pagination = {
        ...(params || {}),
        el: this,
        clickable: true,
        type: 'bullets',
        bulletClass: 'slideshow-list-swiper__bullet',
        bulletActiveClass: 'slideshow-list-swiper__bullet--active',
      };

      reinitModule(swiper.pagination, () => {
        swiper.pagination.init();
        swiper.pagination.render();
        swiper.pagination.update();
      });
    }
  }

  customElements.define('carousel-pagination', CarouselPagination);
}
